import './polyfills'; // MUST be first — Node.js globals shim for simple-peer / readable-stream
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import 'regenerator-runtime/runtime';

// ─────────────────────────────────────────────────────────────
// MockCoach Design Tokens — Professional Corporate Palette
// Single source of truth — use these everywhere instead of
// raw hex values so the whole app stays consistent.
// ─────────────────────────────────────────────────────────────
const BRAND = {
  // Core palette — deep navy primary, professional teal secondary
  primary:   '#0D47A1',   // Deep corporate navy blue
  secondary: '#00796B',   // Complementary professional teal
  accent:    '#D81B60',   // Strong accent for highlights/badges

  // Gradients — navy-to-blue (reserved for headers/hero only)
  gradientPrimary: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
  gradientSubtle:  'linear-gradient(135deg, #EEF2FF 0%, #F5F7FA 100%)',
  gradientAccent:  'linear-gradient(135deg, #2E7D32 0%, #00796B 100%)',
  gradientWarn:    'linear-gradient(135deg, #FF8F00 0%, #C62828 100%)',

  // System colours
  green: '#2E7D32',
  amber: '#FF8F00',
  red:   '#C62828',

  // Neutral surfaces
  surfaceLight: '#F5F7FA',
  border:       '#CFD8DC',
  borderHover:  '#B0BEC5',
};

const theme = createTheme({
  palette: {
    primary: {
      main:         BRAND.primary,
      light:        '#4271D3',
      dark:         '#002171',
      contrastText: '#ffffff',
    },
    secondary: {
      main:         BRAND.secondary,
      light:        '#48A999',
      dark:         '#004C40',
      contrastText: '#ffffff',
    },
    success: {
      main:  BRAND.green,
      light: '#4CAF50',
      dark:  '#1B5E20',
    },
    warning: {
      main:  BRAND.amber,
      light: '#FFB74D',
      dark:  '#E65100',
    },
    error: {
      main:  BRAND.red,
      light: '#EF5350',
      dark:  '#7F0000',
    },
    info: {
      main:  BRAND.primary,
      light: '#4271D3',
      dark:  '#002171',
    },
    text: {
      primary:   '#263238',   // Dark slate — high readability
      secondary: '#546E7A',
      disabled:  '#90A4AE',
    },
    background: {
      default: BRAND.surfaceLight,
      paper:   '#ffffff',
    },
    divider: BRAND.border,
    // Custom token — accessible via theme.palette.brand.*
    brand: BRAND,
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: '#546E7A' },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.5, color: '#546E7A' },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
    caption: { color: '#78909C' },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.06)',
    '0 2px 4px rgba(15,23,42,0.08)',
    '0 4px 8px rgba(15,23,42,0.08)',
    '0 6px 16px rgba(15,23,42,0.10)',
    '0 8px 24px rgba(15,23,42,0.10)',
    '0 12px 32px rgba(15,23,42,0.12)',
    '0 16px 40px rgba(15,23,42,0.12)',
    '0 20px 48px rgba(15,23,42,0.14)',
    '0 24px 56px rgba(15,23,42,0.14)',
    '0 28px 64px rgba(15,23,42,0.16)',
    '0 32px 72px rgba(15,23,42,0.16)',
    '0 36px 80px rgba(15,23,42,0.18)',
    '0 40px 88px rgba(15,23,42,0.18)',
    '0 44px 96px rgba(15,23,42,0.20)',
    '0 48px 104px rgba(15,23,42,0.20)',
    '0 52px 112px rgba(15,23,42,0.22)',
    '0 56px 120px rgba(15,23,42,0.22)',
    '0 60px 128px rgba(15,23,42,0.24)',
    '0 64px 136px rgba(15,23,42,0.24)',
    '0 68px 144px rgba(15,23,42,0.26)',
    '0 72px 152px rgba(15,23,42,0.26)',
    '0 76px 160px rgba(15,23,42,0.28)',
    '0 80px 168px rgba(15,23,42,0.28)',
    '0 84px 176px rgba(15,23,42,0.30)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          background: BRAND.gradientPrimary,
          '&:hover': {
            background: 'linear-gradient(135deg, #0a3d8f 0%, #0d47a1 100%)',
          },
        },
      },
      variants: [
        {
          // Use variant="contained-contrast" for buttons placed on coloured
          // backgrounds (headers, hero sections, gradient panels) where the
          // default gradient primary would be invisible.
          props: { variant: 'contained-contrast' },
          style: {
            color: '#ffffff',
            backgroundColor: '#1976d2',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: '#0d47a1',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(25,118,210,0.4)',
              color: 'rgba(255,255,255,0.7)',
            },
          },
        },
        {
          // Use variant="contained-white" for the primary hero CTA on dark/gradient
          // backgrounds — e.g. "Get Started Free" on the landing page hero.
          props: { variant: 'contained-white' },
          style: {
            color: '#0D47A1',
            backgroundColor: '#ffffff',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#EEF2FF',
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: '#DBEAFE',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255,255,255,0.5)',
              color: 'rgba(13,71,161,0.5)',
            },
          },
        },
        {
          // Use variant="outlined-white" for secondary CTAs on dark/gradient
          // backgrounds — e.g. "Sign In" on the landing page hero.
          props: { variant: 'outlined-white' },
          style: {
            color: '#ffffff',
            backgroundColor: 'transparent',
            border: '1.5px solid rgba(255,255,255,0.6)',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1.5px solid #ffffff',
              boxShadow: 'none',
            },
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(15,23,42,0.07)',
          border: `1px solid ${BRAND.border}`,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(15,23,42,0.10)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(15,23,42,0.07)',
          border: `1px solid ${BRAND.border}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#0F172A',
          backgroundColor: BRAND.surfaceLight,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: BRAND.border,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: BRAND.border,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (process.env.NODE_ENV === 'production') {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New content is available; please refresh.');
                if (window.confirm('New version available! Click OK to refresh.')) {
                  window.location.reload();
                }
              }
            });
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    } else {
      // In development, ensure any existing service workers are unregistered to avoid intercepting /api requests
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          registrations.forEach((registration) => {
            console.log('Unregistering SW in development:', registration);
            registration.unregister();
          });
        })
        .catch((err) => {
          console.log('SW unregister failed: ', err);
        });
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();